import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { di } from 'react-magnetic-di';
import { getThemeColor } from 'state/selectors/help-center';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { setGlobalTheme } from '@atlaskit/tokens/set-global-theme';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { useIsoMorphicLayoutEffect } from '@atlassian/help-center-common-util/hooks';
import type { IThemingContainerProps } from './types';
import { OUTCOME } from './types';

export const ThemingComponent = (props: IThemingContainerProps) => {
	di(setGlobalTheme, useAnalyticsEvents);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { children, themeColor } = props;
	const isAddColorDesignTokenEnabled = FeatureGates.checkGate(
		'add_color_design_token_support_into_help_center',
	);
	const isTypographyTokenMigrationEnabled = FeatureGates.checkGate(
		'enable_typography_token_migration_for_csm',
	);

	const sendAnalyticsEvent = React.useCallback(
		(action: OUTCOME) => {
			const analyticsEvent = createAnalyticsEvent({
				action,
				analyticsType: OPERATIONAL_EVENT_TYPE,
				actionSubject: 'portalThemingBootstrap',
			});
			analyticsEvent.fire();
		},
		[createAnalyticsEvent],
	);

	const themeColorWithoutHash = themeColor.slice(1);

	useIsoMorphicLayoutEffect(() => {
		const setGlobalThemePromise = setGlobalTheme({
			// @ts-expect-error
			light: isAddColorDesignTokenEnabled ? 'light' : '',
			spacing: 'spacing',
			colorMode: 'light',
			// @ts-expect-error
			typography: null,
			...(isTypographyTokenMigrationEnabled &&
				isCSMHelpCenter(getEnv().helpCenterType) && {
					typography: 'typography',
				}),
			...(isAddColorDesignTokenEnabled && {
				UNSAFE_themeOptions: {
					brandColor: `#${themeColorWithoutHash}`,
				},
			}),
		}) as Promise<() => void>;
		setGlobalThemePromise
			.then(() => sendAnalyticsEvent(OUTCOME.SUCCESS))
			.catch(() => sendAnalyticsEvent(OUTCOME.FAILURE));
	}, [sendAnalyticsEvent, themeColor]);

	return <>{children}</>;
};

const selector = createSelector(getThemeColor, (themeColor) => ({ themeColor }));
export const ThemingContainer = connect(selector)(ThemingComponent);
